import React from 'react';
import { useHistory } from 'react-router-dom';
import useBackendApi from '../../hooks/useBackendApi';
import useLocalSuccessErrMsg from '../../hooks/useLocalSuccessErrMsg';
import useSteps from '../../hooks/useSteps';
import ErrorModal from '../../lib/ErrorModal/ErrorModal';
import { IFormSettings } from '../../types/formSettings';
import { IOffer } from '../../types/quote/offer';
import { delay, replaceUrlParamsWithValue } from '../../utils';
import FormWizard from '../FormWizard/FormWizard';
import SwipeTransition from '../SwipeTransition/SwipeTransition';
import CalculatingStep from './components/CalculatingStep/CaclulatingStep';
import InfoStep from './components/InfoStep/InfoStep';
import PricingStep from './components/PricingStep/PricingStep';
import ServicesStep from './components/ServicesStep/ServicesStep';

interface IChangeBookingWizardProps {
  selectedServices?: string[];
  offerId?: string;
  open?: boolean;
  drawerView?: boolean;
  onClose?: (e?: any) => void;
}

const ChangeBookingWizard: React.FC<React.PropsWithChildren<React.PropsWithChildren<IChangeBookingWizardProps>>> = ({
  open,
  onClose,
  offerId,
  drawerView,
  selectedServices = [],
}) => {
  const setpsHeadingsMapping = {
    info: {
      subTitle: 'Change Package',
      title: `Important Info`,
    },
    services: {
      subTitle: 'Services',
      title: 'What services would you like?',
    },
    calculating: {
      subTitle: 'Change Package',
      title: 'Pricing',
    },
    pricing: {
      subTitle: 'Change Package',
      title: 'Pricing',
    },
  };
  const { post, patch, get } = useBackendApi();

  const { step, isLastStep, isFirstStep, index, next, prev, setStep } =
    useSteps(['info', 'services', 'calculating', 'pricing']);
  const history = useHistory();
  const { loading, setLoading, err, setErr, clearErr } =
    useLocalSuccessErrMsg();

  const [formSettings, setFormSettings] = React.useState<IFormSettings>();
  const [services, setServices] = React.useState<string[]>([]);
  const [newRequest, setNewRequest] = React.useState<IOffer>();

  React.useEffect(() => {
    if (selectedServices.length) {
      setServices(selectedServices);
    }
  }, [selectedServices]);

  React.useEffect(() => {
    if (open) {
      const fetchFormSettings = async () => {
        const url = replaceUrlParamsWithValue(
          '/v1/offers/:offerId/providerServices',
          { offerId }
        );
        const res = await get(url, {}, { loader: false });
        setFormSettings(res.data.formSettings);
      };
      fetchFormSettings();
    }
  }, [open]);

  React.useEffect(() => {
    if (step === 'calculating') {
      submitNewPackage();
    }
  }, [step]);

  function handleBack() {
    if (isFirstStep) {
      return onClose?.();
    }
    if (step === 'pricing') {
      return setStep('services');
    }
    prev();
  }

  function handleNext() {
    if (isLastStep) {
      return confirmPackageChange();
    }
    next();
  }

  async function performApiCall(postOrPatch) {
    const fn = (postOrPatch === 'patch' && patch) || post;
    setLoading(true);
    const url = replaceUrlParamsWithValue('/v1/offers/:offerId/changePackage', {
      offerId,
    });
    const res = await fn(
      url,
      { services },
      {
        loader: false,
        onError: (err) => {
          setLoading(false);
          setErr(err.message);
        },
      }
    );
    setLoading(false);
    return res.data;
  }

  async function submitNewPackage() {
    const newRequest = await performApiCall('patch');
    setNewRequest(newRequest);
    await delay(1000);
    next();
  }

  async function confirmPackageChange() {
    const newRequest = await performApiCall('post');
    onClose?.();
    return history.push(`/offers/${newRequest?.offer?._id}`);
  }

  const isFormValid = React.useMemo(() => {
    switch (step) {
      case 'services':
        return services.length;
      default:
        return true;
    }
  }, [step, services]);

  return (
    <FormWizard
      open={open}
      onClose={onClose}
      onBack={handleBack}
      title={setpsHeadingsMapping[step]?.title}
      subTitle={setpsHeadingsMapping[step]?.subTitle}
      onNext={handleNext}
      nextBtnDisabled={!isFormValid}
      nextBtnLabel={((isFirstStep || isLastStep) && 'Continue') || 'Next'}
      hideClearBtn
      drawerView={drawerView}
      loading={loading || step === 'calculating'}
    >
      <SwipeTransition activeStepIndex={index} duration="500">
        <InfoStep />
        <ServicesStep
          formSettings={formSettings}
          services={services}
          onChange={setServices}
        />
        <CalculatingStep />
        <PricingStep pricing={newRequest?.offer?.pricing} />
      </SwipeTransition>

      <ErrorModal
        open={!!err}
        onClose={clearErr}
        message={err}
        size="lg"
        errorMsgClassName="text-gray-700"
      />
    </FormWizard>
  );
};
export default ChangeBookingWizard;
